import { useEffect, useState } from "react";
import { Gear, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TZ_OPTIONS = ["America/Denver", "America/Chicago", "America/New_York", "America/Los_Angeles", "America/Phoenix", "UTC"];

export default function DigestSettings() {
  const { user } = useAuth();
  const uid = user?.user_id;
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!uid) return;
    api.get(`/users/${uid}/digest-preferences`).then((r) => setPrefs(r.data)).catch(() => setPrefs({ digest_frequency: "daily", timezone: "America/Denver" }));
  }, [uid]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/users/${uid}/digest-preferences`, {
        digest_frequency: prefs.digest_frequency, timezone: prefs.timezone });
      setPrefs(r.data);
      toast.success("Digest preferences saved");
    } catch (e) {
      toast.error("Could not save preferences");
    } finally { setSaving(false); }
  };

  if (!prefs) return <div className="py-24 text-center text-sm uppercase tracking-[0.3em] text-muted-foreground animate-pulse">Loading…</div>;

  return (
    <div className="max-w-xl space-y-8 animate-fade-up" data-testid="settings-digest">
      <div className="flex items-start gap-4 pb-6 border-b border-border">
        <div className="h-12 w-12 rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center"><Gear size={22} /></div>
        <div><p className="eyebrow">Settings</p><h1 className="page-title text-[36px] mt-1">Morning digest</h1>
          <p className="text-sm text-muted-foreground mt-2">Control your daily morning-check email. Both operators are subscribed by default.</p></div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold">Delivery frequency</label>
        <Select value={prefs.digest_frequency} onValueChange={(v) => setPrefs({ ...prefs, digest_frequency: v })}>
          <SelectTrigger data-testid="digest-frequency-select" className="max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily (7 AM)</SelectItem>
            <SelectItem value="weekly">Weekly (Mondays)</SelectItem>
            <SelectItem value="off">Off</SelectItem>
          </SelectContent>
        </Select>
        {prefs.digest_frequency === "weekly" && <p className="text-xs text-muted-foreground">You'll receive a weekly summary on Monday at 7 AM in your timezone.</p>}
        {prefs.digest_frequency === "off" && <p className="text-xs text-muted-foreground">You won't receive the digest. You can re-enable anytime.</p>}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold">Timezone</label>
        <Select value={prefs.timezone} onValueChange={(v) => setPrefs({ ...prefs, timezone: v })}>
          <SelectTrigger data-testid="digest-timezone-select" className="max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{TZ_OPTIONS.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Button onClick={save} disabled={saving} data-testid="digest-save-button"><FloppyDisk size={16} className="mr-2" /> {saving ? "Saving…" : "Save preferences"}</Button>
    </div>
  );
}
